// src/components/admin/BillingPlansSimple.js
//
// Simplified single-plan billing page. The full multi-tier `BillingPlans.js`
// is preserved for future use — once a multi-tier rollout is wanted, swap the
// route in AdminPortal back to BillingPlans.
import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { TIER_CONFIG, calculatePlanAmount } from '../../services/razorpay.service';
import razorpayService from '../../services/razorpay.service';
import {
    Sparkles, Check, Shield, CreditCard, Users, Loader2, CheckCircle,
    AlertTriangle, Bot, Minus, Plus
} from 'lucide-react';

const PLAN_TIER = 'pro';

const BillingPlansSimple = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const proConfig = TIER_CONFIG[PLAN_TIER];

    const [subscription, setSubscription] = useState(null);
    const [loadingSub, setLoadingSub] = useState(true);
    const [employeeCount, setEmployeeCount] = useState(proConfig.baseEmployees);
    const [processing, setProcessing] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadSubscription();
    }, []);

    const loadSubscription = async () => {
        try {
            setLoadingSub(true);
            const sub = await razorpayService.getSubscriptionDetails();
            setSubscription(sub);
            if (sub?.employeeLimit && sub.status !== 'trial' && sub.status !== 'expired') {
                setEmployeeCount(sub.employeeLimit);
            }
        } catch (err) {
            console.error('Failed to load subscription:', err);
        } finally {
            setLoadingSub(false);
        }
    };

    const pricing = useMemo(
        () => calculatePlanAmount(PLAN_TIER, employeeCount),
        [employeeCount]
    );

    const isActivePro = subscription
        && (subscription.tier === PLAN_TIER || subscription.planType === PLAN_TIER)
        && subscription.status !== 'trial'
        && subscription.status !== 'expired';

    const adjustCount = (delta) => {
        setEmployeeCount(prev => {
            const next = prev + delta;
            return Math.max(1, Math.min(proConfig.maxEmployees, next));
        });
    };

    const handleCountChange = (value) => {
        const n = parseInt(value, 10);
        if (Number.isNaN(n)) return;
        setEmployeeCount(Math.max(1, Math.min(proConfig.maxEmployees, n)));
    };

    const processPayment = async () => {
        setProcessing(true);
        setError(null);
        try {
            const orderResponse = await razorpayService.createRazorpayOrder({
                planType: PLAN_TIER,
                planName: proConfig.label,
                employeeCount,
                amount: pricing.amountInPaise,
                billingCycle: 'monthly',
                isUpgrade: !!isActivePro
            });

            const options = {
                order_id: orderResponse.orderId,
                amount: orderResponse.amount,
                currency: 'INR',
                name: 'OakHR',
                description: `Pro Plan — ${employeeCount} employees`,
                image: '/oakhr-logo.png',
                handler: async (response) => {
                    try {
                        const result = await razorpayService.verifyRazorpayPayment({
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_signature: response.razorpay_signature,
                            planType: PLAN_TIER,
                            employeeCount
                        });
                        setPaymentSuccess({ employeeCount, ...pricing, ...result });
                        setProcessing(false);
                        loadSubscription();
                    } catch (err) {
                        setError('Payment verification failed. Please contact support.');
                        setProcessing(false);
                    }
                },
                prefill: { name: '', email: '', contact: '' },
                notes: { planType: PLAN_TIER, employeeCount: employeeCount.toString() },
                theme: { color: isDark ? '#7c3aed' : '#6366f1' },
                modal: { ondismiss: () => setProcessing(false) }
            };

            await razorpayService.initiateRazorpayPayment(options, () => {}, () => {
                setError('Payment failed. Please try again.');
                setProcessing(false);
            });
        } catch (err) {
            console.error('Payment error:', err);
            setError(err.response?.data?.error || err.response?.data?.details || 'Failed to initiate payment. Please try again.');
            setProcessing(false);
        }
    };

    const ctaLabel = isActivePro ? 'Update Subscription' : 'Subscribe Now';
    const isTrial = !subscription || subscription.status === 'trial' || subscription.status === 'expired';

    if (loadingSub) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
            </div>
        );
    }

    if (paymentSuccess) {
        return (
            <div className={`min-h-screen p-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <div className="max-w-2xl mx-auto mt-16">
                    <div className={`rounded-2xl p-10 text-center shadow-xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
                        <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center mb-4">
                            <CheckCircle className="w-8 h-8 text-white" />
                        </div>
                        <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            You're on Pro
                        </h2>
                        <p className={`text-sm mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            {paymentSuccess.employeeCount} employees · ₹{paymentSuccess.totalAmount?.toLocaleString('en-IN')} billed
                        </p>
                        <button
                            onClick={() => setPaymentSuccess(null)}
                            className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 text-white font-medium hover:from-violet-700 hover:to-purple-700"
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen p-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs font-medium mb-3">
                        <Sparkles className="w-3.5 h-3.5" />
                        Pro Subscription
                    </div>
                    <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {isActivePro ? 'Manage Your Subscription' : 'Subscribe to OakHR'}
                    </h1>
                    <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Everything you need to run HR for your team. Simple, monthly billing.
                    </p>
                </div>

                {/* Current status banner */}
                {isActivePro && (
                    <div className={`mb-6 p-4 rounded-xl border ${isDark ? 'bg-emerald-900/20 border-emerald-800 text-emerald-200' : 'bg-emerald-50 border-emerald-200 text-emerald-800'} flex items-center gap-3`}>
                        <CheckCircle className="w-5 h-5 flex-shrink-0" />
                        <div className="text-sm">
                            You're currently on <strong>Pro</strong> with {subscription?.employeeLimit} employees.
                            {subscription?.endDate && (
                                <> Renews on <strong>{new Date(subscription.endDate).toLocaleDateString('en-IN')}</strong>.</>
                            )}
                        </div>
                    </div>
                )}
                {isTrial && (
                    <div className={`mb-6 p-4 rounded-xl border ${isDark ? 'bg-amber-900/20 border-amber-800 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-800'} flex items-center gap-3`}>
                        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                        <div className="text-sm">
                            You're on the free trial (up to 2 employees). Subscribe to add more.
                        </div>
                    </div>
                )}

                {/* Plan card */}
                <div className={`tour-billing-plan rounded-2xl overflow-hidden shadow-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    {/* Header strip */}
                    <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs uppercase tracking-wider opacity-80">Pro Plan</p>
                                <h2 className="text-2xl font-bold mt-1">All-in-one HR for growing teams</h2>
                            </div>
                            <div className="text-right">
                                <p className="text-xs opacity-80">Starts at</p>
                                <p className="text-2xl font-bold">₹{proConfig.baseFee.toLocaleString('en-IN')}<span className="text-sm font-normal opacity-80">/mo</span></p>
                            </div>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="p-6 space-y-6">
                        {/* Employee count selector */}
                        <div className="tour-billing-count">
                            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                How many employees?
                            </label>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => adjustCount(-1)}
                                    disabled={employeeCount <= 1}
                                    className={`w-10 h-10 rounded-lg border flex items-center justify-center transition disabled:opacity-40 ${isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                >
                                    <Minus className="w-4 h-4" />
                                </button>
                                <input
                                    type="number"
                                    min={1}
                                    max={proConfig.maxEmployees}
                                    value={employeeCount}
                                    onChange={(e) => handleCountChange(e.target.value)}
                                    className={`w-24 h-10 rounded-lg border text-center font-semibold ${isDark ? 'bg-gray-900 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                />
                                <button
                                    onClick={() => adjustCount(1)}
                                    disabled={employeeCount >= proConfig.maxEmployees}
                                    className={`w-10 h-10 rounded-lg border flex items-center justify-center transition disabled:opacity-40 ${isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Up to {proConfig.maxEmployees} employees
                                </span>
                            </div>
                        </div>

                        {/* Quick highlights */}
                        <div className="tour-billing-highlights grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                                <Users className="w-5 h-5 text-violet-500 mb-2" />
                                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Employees</p>
                                <p className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{employeeCount} active</p>
                            </div>
                            <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                                <Bot className="w-5 h-5 text-violet-500 mb-2" />
                                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>AI Tool Credits</p>
                                <p className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{proConfig.aiCredits}/month</p>
                            </div>
                            <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                                <CreditCard className="w-5 h-5 text-violet-500 mb-2" />
                                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Billing</p>
                                <p className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Monthly</p>
                            </div>
                        </div>

                        {/* Inclusion line */}
                        <div className={`flex items-start gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            <Check className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" />
                            <span>All HR features included — attendance, leave, payroll, performance, documents, reports & AI assistant.</span>
                        </div>

                        {/* Pricing breakdown */}
                        <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                            <div className="flex justify-between text-sm mb-2">
                                <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                                    Base ({proConfig.baseEmployees} employees)
                                </span>
                                <span className={isDark ? 'text-gray-200' : 'text-gray-900'}>
                                    ₹{proConfig.baseFee.toLocaleString('en-IN')}
                                </span>
                            </div>
                            {pricing.extraEmployees > 0 && (
                                <div className="flex justify-between text-sm mb-2">
                                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                                        +{pricing.extraEmployees} extra × ₹{proConfig.perEmployee}
                                    </span>
                                    <span className={isDark ? 'text-gray-200' : 'text-gray-900'}>
                                        ₹{(pricing.extraEmployees * proConfig.perEmployee).toLocaleString('en-IN')}
                                    </span>
                                </div>
                            )}
                            <div className="flex justify-between text-sm mb-2">
                                <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>GST (18%)</span>
                                <span className={isDark ? 'text-gray-200' : 'text-gray-900'}>
                                    ₹{pricing.gst.toLocaleString('en-IN')}
                                </span>
                            </div>
                            <div className={`flex justify-between text-base font-bold mt-3 pt-3 border-t ${isDark ? 'border-gray-700 text-white' : 'border-gray-200 text-gray-900'}`}>
                                <span>Total / month</span>
                                <span>₹{pricing.totalAmount.toLocaleString('en-IN')}</span>
                            </div>
                        </div>

                        {/* Error */}
                        {error && (
                            <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${isDark ? 'bg-red-900/20 border border-red-800 text-red-200' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span>{error}</span>
                            </div>
                        )}

                        {/* CTA */}
                        <button
                            onClick={processPayment}
                            disabled={processing}
                            className="tour-billing-subscribe w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold text-base shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition"
                        >
                            {processing ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Processing…
                                </>
                            ) : (
                                <>
                                    <Sparkles className="w-5 h-5" />
                                    {ctaLabel}
                                </>
                            )}
                        </button>

                        <div className={`flex items-center justify-center gap-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                            <Shield className="w-3.5 h-3.5" />
                            Secure payment via Razorpay · Cancel anytime
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BillingPlansSimple;
